/**
 * Krok UI4 — admin klient veřejné stránky nemovitosti (SR5, `/n/<slug>`).
 *
 * Jádro i izolace jsou hotové od SR5/SR6 — tenhle soubor jen zavolá cesty
 * `/v1/crm/public-listings*`, nic nevymýšlí. `active` (zapnuto/vypnuto) je
 * krok UI4, zbytek (`reportPublished`, `slug`) SR5.
 */

import type { ApiPaths } from '@reos/api-client';
import { apiClient } from './api.js';
import { DomainApiError, unwrap } from './api-error.js';

export type PublicListingPage =
  ApiPaths['/v1/crm/public-listings/{caseId}']['get']['responses']['200']['content']['application/json'];

/** `null` = stránka pro tuhle zakázku ještě nikdy nebyla zapnutá (404 z API). */
export async function fetchPublicListingPage(caseId: string): Promise<PublicListingPage | null> {
  try {
    return await unwrap(apiClient.GET('/v1/crm/public-listings/{caseId}', { params: { path: { caseId } } }));
  } catch (error) {
    if (error instanceof DomainApiError && error.status === 404) return null;
    throw error;
  }
}

/** Zapnutí — idempotentní (existující vypnutou stránku reaktivuje na STEJNÉM slugu). */
export async function enablePublicListingPage(caseId: string): Promise<PublicListingPage> {
  return unwrap(apiClient.POST('/v1/crm/public-listings', { body: { caseId } }));
}

export async function setPublicListingPageActive(caseId: string, active: boolean): Promise<PublicListingPage> {
  return unwrap(
    apiClient.PATCH('/v1/crm/public-listings/{caseId}/active', { params: { path: { caseId } }, body: { active } }),
  );
}

/**
 * Krok NAP10 (A23): přepínače webu kanceláře. Jedno volání pro oba příznaky,
 * nezadaný příznak server NEMĚNÍ — jinak by zapnutí jednoho přepsalo druhý.
 */
export async function setPublicListingOfficeSiteFlags(
  caseId: string,
  flags: { readonly showOnOfficeSite?: boolean; readonly referencePublished?: boolean },
): Promise<PublicListingPage> {
  return unwrap(
    apiClient.PATCH('/v1/crm/public-listings/{caseId}/office-site', {
      params: { path: { caseId } },
      body: flags,
    }),
  );
}

/**
 * Krok SCHV1 — schválení inzerátu majitelem. Stav počítá SERVER (včetně
 * „inzerát se po souhlasu změnil"), administrace si pravidlo neopisuje.
 */
export type OwnerApproval =
  ApiPaths['/v1/crm/public-listings/{caseId}/owner-approval']['get']['responses']['200']['content']['application/json'];

export type IssuedOwnerLink =
  ApiPaths['/v1/crm/public-listings/{caseId}/owner-approval']['post']['responses']['201']['content']['application/json'];

/** `null` = stránka pro tuhle zakázku ještě není zapnutá (404 z API). */
export async function fetchOwnerApproval(caseId: string): Promise<OwnerApproval | null> {
  try {
    return await unwrap(
      apiClient.GET('/v1/crm/public-listings/{caseId}/owner-approval', { params: { path: { caseId } } }),
    );
  } catch (error) {
    if (error instanceof DomainApiError && error.status === 404) return null;
    throw error;
  }
}

/**
 * Pošle majiteli NOVÝ odkaz (předchozí přestane platit). `email` nechává
 * makléř prázdný, když chce odkaz jen zkopírovat nebo poslat přes WhatsApp.
 */
export async function sendOwnerApproval(
  caseId: string,
  input: { readonly email?: string | undefined } = {},
): Promise<IssuedOwnerLink> {
  return unwrap(
    apiClient.POST('/v1/crm/public-listings/{caseId}/owner-approval', {
      params: { path: { caseId } },
      body: input.email === undefined || input.email.trim() === '' ? {} : { email: input.email.trim() },
    }),
  );
}

export async function setPublicListingReportPublished(
  caseId: string,
  reportPublished: boolean,
): Promise<PublicListingPage> {
  return unwrap(
    apiClient.PATCH('/v1/crm/public-listings/{caseId}', { params: { path: { caseId } }, body: { reportPublished } }),
  );
}
